import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Input } from "reactstrap";
import { diffInternals } from "../../shared/diff";

const { fkValue } = diffInternals;

/**
 * Camera routing picker — mirrors the legacy ChannelForm pattern.
 *
 * The user toggles two routing intents (in_production, in_simulation) and picks
 * a segment; we look up the unique AlertChannel matching them (plus
 * is_enabled + is_allowed_for_channel) and write its id into the camera.
 *
 * The Camera entity only stores `alert_channel` (int id) — the routing flags
 * exist on the AlertChannel reference table.
 */

const DEFAULT_SEGMENT = "store";

function deriveRouting(channel, segments) {
  if (channel) {
    return {
      in_production: !!channel.is_production,
      in_simulation: !!channel.is_simulation,
      segment: channel.segment || segments[0]?.key || DEFAULT_SEGMENT,
    };
  }
  return {
    in_production: true,
    in_simulation: false,
    segment: segments[0]?.key || DEFAULT_SEGMENT,
  };
}

function findMatches(channels, routing) {
  return channels.filter(
    (ac) =>
      ac.is_enabled &&
      ac.is_allowed_for_channel &&
      !!ac.is_production === routing.in_production &&
      !!ac.is_simulation === routing.in_simulation &&
      (ac.segment || "") === routing.segment
  );
}

export default function AlertChannelRouting({ camera, alertChannels, segments, onChange }) {
  // Normalize the FK value: the API may return alert_channel as a plain int OR a
  // nested {id, …} object. fkValue smooths over both shapes.
  const currentChannelId = fkValue(camera.alert_channel);
  const currentChannel = alertChannels.find((ac) => ac.id === currentChannelId) || null;
  const [routing, setRouting] = useState(() => deriveRouting(currentChannel, segments));
  const lastAppliedRef = useRef(currentChannelId);

  // External changes to camera.alert_channel (load/revert/save) → resync local intent.
  // Skips changes we triggered ourselves (lastAppliedRef === currentChannelId).
  useEffect(() => {
    if (currentChannelId === lastAppliedRef.current) return;
    const ch = alertChannels.find((ac) => ac.id === currentChannelId) || null;
    setRouting(deriveRouting(ch, segments));
    lastAppliedRef.current = currentChannelId;
  }, [currentChannelId, alertChannels, segments]);

  const apply = (patch) => {
    const next = { ...routing, ...patch };
    setRouting(next);
    const matches = findMatches(alertChannels, next);
    const newId = matches.length === 1 ? matches[0].id : null;
    lastAppliedRef.current = newId;
    onChange(newId);
  };

  const matches = findMatches(alertChannels, routing);
  const matchCount = matches.length;
  let resolved;
  if (matchCount === 1) {
    resolved = { kind: "ok", channel: matches[0] };
  } else if (matchCount === 0) {
    resolved = { kind: "none" };
  } else {
    resolved = { kind: "ambiguous", channels: matches };
  }

  return (
    <div>
      <div className="d-flex align-items-center" style={{ gap: 18, flexWrap: "wrap" }}>
        <label className="d-flex align-items-center mb-0" style={{ gap: 6, fontSize: "0.85rem" }}>
          <Input
            type="checkbox"
            checked={routing.in_production}
            onChange={(e) => apply({ in_production: e.target.checked })}
            style={{ position: "static", margin: 0 }}
          />
          In production
        </label>
        <label className="d-flex align-items-center mb-0" style={{ gap: 6, fontSize: "0.85rem" }}>
          <Input
            type="checkbox"
            checked={routing.in_simulation}
            onChange={(e) => apply({ in_simulation: e.target.checked })}
            style={{ position: "static", margin: 0 }}
          />
          In simulation
        </label>
        <label className="d-flex align-items-center mb-0" style={{ gap: 6, fontSize: "0.85rem" }}>
          Segment:
          <Input
            type="select"
            bsSize="sm"
            className="custom-select"
            value={routing.segment}
            onChange={(e) => apply({ segment: e.target.value })}
            style={{ width: "auto", minWidth: 120 }}
          >
            {segments.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </Input>
        </label>
      </div>

      <div
        style={{
          marginTop: 10,
          padding: "6px 10px",
          borderRadius: 4,
          background: resolved.kind === "ok" ? "#0f5132" : "#5a2026",
          color: "#f0f0f0",
          fontSize: "0.82rem",
        }}
      >
        {resolved.kind === "ok" && (
          <>
            <span style={{ opacity: 0.7 }}>Resolved channel:</span> <strong>{resolved.channel.name}</strong>{" "}
            <span style={{ opacity: 0.7, fontFamily: "monospace" }}>(id {resolved.channel.id})</span>
          </>
        )}
        {resolved.kind === "none" && (
          <>
            ⚠️ No alert channel matches these criteria — camera will be saved with <code>alert_channel: null</code>
          </>
        )}
        {resolved.kind === "ambiguous" && (
          <>
            ⚠️ {matchCount} channels match these criteria ({resolved.channels.map((c) => c.name).join(", ")}) — pick a
            more specific combination
          </>
        )}
      </div>
    </div>
  );
}

AlertChannelRouting.propTypes = {
  camera: PropTypes.shape({
    public_id: PropTypes.string.isRequired,
    alert_channel: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({ id: PropTypes.number })]),
  }).isRequired,
  alertChannels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      is_enabled: PropTypes.bool,
      is_allowed_for_channel: PropTypes.bool,
      is_production: PropTypes.bool,
      is_simulation: PropTypes.bool,
      segment: PropTypes.string,
    })
  ).isRequired,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      key: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};
